import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ReactCrop from 'react-image-crop';
import { Border, Shadow, Type } from '../app/StyleGuide';
import { Container } from './Layout';
import { Label } from './FormElements';

const Preview = styled.div`
  canvas {
    border-radius: ${props => (props.isCircularCrop ? '50%' : Border.radius)};
    box-shadow: ${Shadow.regular};
    width: 100%;
    min-width: 100%;
  }
`;

const DimensionLabel = styled.p`
  font-size: ${Type.Scale._1};
  font-style: italic;
`;

const ImageCropAndPreview = ({ image, onCropUpdate, aspectRatio = 16 / 9, isCircularCrop }) => {
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [crop, setCrop] = useState({ unit: '%', width: 100, aspect: aspectRatio });
  const [originalImageSize, setOriginalImageSize] = useState({ width: '', height: '' });
  const [outputImageSize, setOutputImageSize] = useState({ width: '', height: '' });
  // Increase pixel density for crop preview quality on retina screens.
  const pixelRatio = window.devicePixelRatio || 1;
  const getCroppedImage = (image, cropProps) => {
    const tmpCanvas = document.createElement('canvas');
    tmpCanvas.width = cropProps.width;
    tmpCanvas.height = cropProps.height;
    const ctx = tmpCanvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
      image,
      cropProps.x,
      cropProps.y,
      cropProps.width,
      cropProps.height,
      0,
      0,
      cropProps.width,
      cropProps.height
    );
    return tmpCanvas;
  };
  const onLoad = useCallback(img => {
    imgRef.current = img;
    setOriginalImageSize({ width: img.naturalWidth.toFixed(), height: img.naturalHeight.toFixed() });
  }, []);
  const onCropComplete = crop => {
    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');
    canvas.width = crop.width * pixelRatio;
    canvas.height = crop.height * pixelRatio;
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';
    const scaled = {
      x: crop.x * scaleX,
      y: crop.y * scaleY,
      width: Math.ceil(crop.width * scaleX),
      height: Math.ceil(crop.height * scaleY),
    };
    ctx.drawImage(image, scaled.x, scaled.y, scaled.width, scaled.height, 0, 0, crop.width, crop.height);
    setOutputImageSize({ width: scaled.width, height: scaled.height });
    onCropUpdate(() => getCroppedImage.bind(null, image, scaled));
  };

  return (
    <Container>
      <div className="grid-container grid-col-4 grid-sm-col-8 grid-gap-24">
        <div className="grid-col-span-4 grid-sm-col-span-5">
          <Label>Crop Image</Label>
          <p>Drag and resize the rectangle to update the preview</p>
          <ReactCrop
            src={image}
            onImageLoaded={onLoad}
            crop={crop}
            onChange={c => setCrop(c)}
            onComplete={onCropComplete}
            circularCrop={isCircularCrop}
          />
          <DimensionLabel>
            Actual image size: {originalImageSize.width} x {originalImageSize.height}
          </DimensionLabel>
        </div>
        <Preview className="grid-col-span-4 grid-sm-col-span-3" isCircularCrop={isCircularCrop}>
          <Label>Preview</Label>
          <canvas ref={previewCanvasRef} />
          <DimensionLabel>
            Output image size: {outputImageSize.width} x {outputImageSize.height}
          </DimensionLabel>
        </Preview>
      </div>
    </Container>
  );
};

ImageCropAndPreview.propTypes = {
  image: PropTypes.string.isRequired,
  onCropUpdate: PropTypes.func.isRequired,
  aspectRatio: PropTypes.number,
  isCircularCrop: PropTypes.bool,
};

export default ImageCropAndPreview;
